<div class="schedule-details-layout">
  <section class="roster-section" aria-labelledby="schedule-roster-heading">
    <div class="section-heading-row">
      <div class="section-heading" id="schedule-roster-heading">
        <mat-icon>group</mat-icon>
        <span>DC Roster</span>
        <span class="roster-heading-count">
          {{ details?.rosterAssigned ?? row.rosterAssigned }}/{{
            details?.rosterTotal ?? row.rosterTotal
          }}
        </span>
      </div>
      <div class="roster-actions" *ngIf="hasWritePermission">
        <button
          mat-stroked-button
          type="button"
          (click)="confirmClearRoster()"
          [disabled]="!!actionInFlight"
        >
          <mat-icon>person_off</mat-icon>
          Clear Roster
        </button>
        <button
          mat-stroked-button
          color="warn"
          type="button"
          (click)="confirmDeleteRoster()"
          [disabled]="!!actionInFlight"
        >
          <mat-icon>delete_outline</mat-icon>
          Delete Roster
        </button>
      </div>
    </div>

    <div class="details-loading" *ngIf="loading" aria-live="polite">
      <mat-spinner diameter="30"></mat-spinner>
      <span>Loading DC roster...</span>
    </div>

    <div class="details-error" *ngIf="!loading && errorMessage" role="alert">
      <mat-icon>error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
    </div>

    <div
      class="details-empty"
      *ngIf="!loading && !errorMessage && details?.events?.length === 0"
    >
      This roster has no DC events.
    </div>

    <div
      class="event-card-grid"
      *ngIf="!loading && !errorMessage && details?.events?.length"
    >
      <article
        class="event-card"
        *ngFor="
          let event of details.events;
          let i = index;
          trackBy: trackByEventId
        "
        [attr.aria-labelledby]="'schedule-event-' + event.eventId"
      >
        <header>
          <div>
            <h3 [id]="'schedule-event-' + event.eventId">{{ event.code }}</h3>
            <span class="event-status">{{ event.status || 'No status' }}</span>
          </div>
        </header>

        <app-collector-input
          [auth0Id]="event.collectorAuthId"
          [displayName]="event.collectorName ?? ''"
          [disabled]="
            !hasWritePermission ||
            isEventMutating(event.eventId) ||
            !!actionInFlight
          "
          [allowForceSelection]="true"
          [eligibilityContext]="getCollectorEligibilityContext(event, i)"
          (auth0IdChange)="onCollectorChanged(event, $event)"
        ></app-collector-input>

        <div class="event-metric">
          <div class="metric-heading">
            <span>Progress</span>
            <strong>{{ metricLabel(event.metric) }}</strong>
          </div>
          <div
            class="metric-track"
            *ngIf="metricPercentage(event.metric) !== null"
            role="progressbar"
            aria-label="Event progress"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-valuenow]="metricPercentage(event.metric)"
          >
            <span [style.width.%]="metricPercentage(event.metric)"></span>
          </div>
        </div>

        <footer>
          <div>
            <span class="card-label">Last input</span>
            <span [matTooltip]="lastInputTooltip(event)">
              {{ lastInputLabel(event) }}
            </span>
          </div>
          <button
            mat-button
            type="button"
            class="history-button"
            (click)="openHistory(event)"
          >
            <mat-icon>history</mat-icon>
            History
          </button>
        </footer>
      </article>
    </div>
  </section>

  <aside class="status-section" aria-labelledby="schedule-status-heading">
    <div class="section-heading" id="schedule-status-heading">
      <mat-icon>info</mat-icon>
      <span>Status &amp; issues</span>
    </div>
    <dl class="status-grid">
      <div>
        <dt>Collection Status</dt>
        <dd>
          <app-game-collection-status
            [status]="row.collectionStatus"
          ></app-game-collection-status>
        </dd>
      </div>
      <div>
        <dt>Coverage</dt>
        <dd>
          {{ details?.rosterAssigned ?? row.rosterAssigned }} /
          {{ details?.rosterTotal ?? row.rosterTotal }}
        </dd>
      </div>
      <div>
        <dt>Team Leader</dt>
        <dd>{{
          row.teamLeaderName || row.teamLeaderAuthId || 'Unassigned'
        }}</dd>
      </div>
      <div>
        <dt>Assignment</dt>
        <dd>{{ row.assignmentStatus }}</dd>
      </div>
      <div>
        <dt>Auto-assign</dt>
        <dd>{{ row.autoAssignStatus }}</dd>
      </div>
      <div
        class="overall-progress-row"
        tabindex="0"
        [matTooltip]="overallProgressTooltip()"
        matTooltipClass="schedule-progress-tooltip"
        matTooltipPosition="above"
        [matTooltipShowDelay]="200"
        [attr.aria-label]="overallProgressTooltip()"
      >
        <dt>
          Overall weighted Progress
          <mat-icon class="progress-help-icon" aria-hidden="true"
            >info_outline</mat-icon
          >
        </dt>
        <dd>{{ details?.progressPercentage ?? row.progressPercentage }}%</dd>
        <div
          class="overall-progress-track"
          role="progressbar"
          aria-label="Overall weighted progress"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="
            details?.progressPercentage ?? row.progressPercentage
          "
        >
          <span
            [style.width.%]="
              details?.progressPercentage ?? row.progressPercentage
            "
          ></span>
        </div>
      </div>
    </dl>

    <div class="issue-panel warning-panel" *ngIf="row.warnings?.length">
      <div class="issue-heading">
        <mat-icon>warning</mat-icon>
        Warnings
      </div>
      <ul>
        <li *ngFor="let warning of row.warnings">{{ warning }}</li>
      </ul>
    </div>

    <div class="issue-panel failure-panel" *ngIf="row.failureReasons?.length">
      <div class="issue-heading">
        <mat-icon>error_outline</mat-icon>
        Auto-assign failed
      </div>
      <ul>
        <li *ngFor="let reason of row.failureReasons">{{ reason }}</li>
      </ul>
    </div>

    <button
      *ngIf="hasWritePermission && canMarkAssigned"
      mat-stroked-button
      type="button"
      class="mark-assigned-button"
      (click)="requestMarkAssigned()"
      [disabled]="markingAssigned"
    >
      <mat-icon>task_alt</mat-icon>
      Mark as assigned
    </button>
  </aside>
</div>
